// Rolling log of recent EDITOR actions, surfaced to the Aidea Assistant so the
// agent knows what the user just did ("make the video you just added shorter",
// "the image I generated"). Written to a window global that ChatPanel forwards
// as the `X-Aidea-Recent-Actions` request header — mirroring the one-way
// selected-element / navId context. Best-effort, capped, never throws, never
// blocks dispatch.

const MAX = 8;
const KEY = "__aideaRecentActions";

// Plugin name → friendly noun for the human string.
const PLUGIN_LABEL = {
  HotspotImages: "an image",
  EnrichedPlayer: "a video",
  EnrichedAudio: "an audio narration",
  BasicAudio: "an audio narration",
  BasicText: "a text element",
};

// Record one human-readable recent action, capped to the last MAX entries.
export function recordAction(text) {
  if (typeof window === "undefined" || !text) return;
  try {
    const list = Array.isArray(window[KEY]) ? window[KEY] : (window[KEY] = []);
    list.push({ at: Date.now(), text: String(text).slice(0, 120) });
    if (list.length > MAX) list.splice(0, list.length - MAX);
  } catch {
    /* non-fatal — recent-actions context is best-effort */
  }
}

// Current buffer (for the chat header). Returns [] when empty.
export function getRecentActions() {
  if (typeof window === "undefined") return [];
  return Array.isArray(window[KEY]) ? window[KEY] : [];
}

// Map a small ALLOW-LIST of Redux actions → human strings. Returns null to skip
// (most actions — we deliberately don't log every drag/resize/scene-switch).
function describeAction(action) {
  if (!action || typeof action.type !== "string") return null;
  const p = action.payload || {};
  switch (action.type) {
    case "ADD_BOX": {
      const ip = p.initialParams || {};
      const label = PLUGIN_LABEL[ip.name] || "an element";
      return `Added ${label} to the scene`;
    }
    case "DELETE_BOX":
      return "Deleted an element";
    case "ADD_NAV_ITEM":
      return "Added a scene";
    case "DELETE_NAV_ITEM":
      return "Deleted a scene";
    default:
      return null;
  }
}

// Redux middleware: after an allow-listed action reduces, record a human string.
// Purely observational — passes every action through unchanged.
export const recentActionsMiddleware = () => (next) => (action) => {
  const result = next(action);
  try {
    const text = describeAction(action);
    if (text) recordAction(text);
  } catch {
    /* never break dispatch */
  }
  return result;
};
